/**
 * 头像/LOGO 上传弹窗
 * 支持自定义标题、正方形裁切区、正方形与圆形预览，确定后上传并回调 URL
 */
import { useRef, useState, useCallback } from 'react';
import { Button, Description, Modal } from '@heroui/react';
import { ImageCropper } from '@hs/ui';
import type { ImageCropperRef } from '@hs/ui';
import { apiClient } from '@hs/shared/utils/api-client';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

export interface AvatarUploadDialogProps {
  /** 是否打开 */
  open: boolean;
  /** 打开状态变更 */
  onOpenChange: (open: boolean) => void;
  /** 自定义标题 */
  title?: string;
  /** 确定后回调，传入上传返回的 URL */
  onConfirm: (url: string) => void;
}

const ACCEPT_IMAGE = 'image/jpeg,image/png,image/webp,image/gif';

export function AvatarUploadDialog({
  open,
  onOpenChange,
  title = '上传LOGO',
  onConfirm,
}: AvatarUploadDialogProps) {
  const cropperRef = useRef<ImageCropperRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;

  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [cropPreviewUrl, setCropPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const resetState = useCallback(() => {
    setImageSrc(null);
    setCropPreviewUrl(null);
    setUploadError(null);
  }, []);

  const handleModalOpenChange = useCallback(
    (next: boolean) => {
      if (!next) resetState();
      onOpenChangeRef.current(next);
    },
    [resetState]
  );

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result as string);
      setCropPreviewUrl(null);
    };
    reader.readAsDataURL(file);
    setUploadError(null);
    e.target.value = '';
  }, []);

  const handleComplete = useCallback(
    (file: File) => {
      setIsUploading(true);
      apiClient
        .uploadFileSimple({ file, urlType: 'permanent' })
        .then((res) => {
          onConfirm(res.url);
          onOpenChangeRef.current(false);
          resetState();
        })
        .catch((err) => {
          setUploadError(err instanceof Error ? err.message : '上传失败');
        })
        .finally(() => setIsUploading(false));
    },
    [onConfirm, resetState]
  );

  const handleConfirm = useCallback(() => {
    if (!imageSrc) return;
    setUploadError(null);
    cropperRef.current?.complete();
  }, [imageSrc]);

  const handleCancel = useCallback(() => {
    onOpenChangeRef.current(false);
    resetState();
  }, [resetState]);

  return (
    <Modal isOpen={open} onOpenChange={handleModalOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" scroll="inside" size="lg">
          <Modal.Dialog className="max-h-[90vh] max-w-2xl">
            <Modal.Header className="gap-1">
              <Modal.Heading className="text-xl font-medium">{title}</Modal.Heading>
              <Description className="text-muted text-sm">
                选择图片后在左侧调整裁切区域，右侧可预览方形与圆形效果。
              </Description>
            </Modal.Header>

            <Modal.Body className="flex flex-col gap-4 p-4">
              <div className="flex shrink-0 flex-col">
                <Button
                  type="button"
                  variant="secondary"
                  className="w-fit"
                  onPress={() => fileInputRef.current?.click()}
                >
                  上传图片
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPT_IMAGE}
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>

              <div className="flex min-h-0 shrink-0 flex-row gap-6">
                {imageSrc ? (
                  <div
                    className="h-[320px] w-[320px] shrink-0 overflow-hidden rounded-[var(--field-radius)] border border-[var(--border)]"
                    style={{ background: 'var(--background)' }}
                  >
                    <ImageCropper
                      ref={cropperRef}
                      image={imageSrc}
                      mode="avatar"
                      onComplete={handleComplete}
                      onCropPreview={setCropPreviewUrl}
                      className="h-full w-full"
                    />
                  </div>
                ) : (
                  <div
                    className="flex h-[320px] w-[320px] shrink-0 items-center justify-center rounded-[var(--field-radius)] border border-[var(--border)] text-sm text-muted"
                    style={{ background: 'color-mix(in oklch, var(--surface-secondary) 70%, transparent)' }}
                  >
                    请先点击「上传图片」选择图片
                  </div>
                )}

                <div
                  className="flex min-h-[320px] min-w-0 flex-1 items-center justify-center rounded-[var(--field-radius)]"
                  style={{ background: 'color-mix(in oklch, var(--surface-secondary) 35%, transparent)' }}
                >
                  <div className="flex flex-col items-center gap-4">
                    <div className="flex flex-col items-center gap-2">
                      <div
                        className="h-24 w-24 overflow-hidden rounded-[var(--field-radius)] border border-[var(--border)]"
                        style={{ background: 'color-mix(in oklch, var(--surface-secondary) 50%, transparent)' }}
                      >
                        {(cropPreviewUrl ?? imageSrc) && (
                          <img
                            src={cropPreviewUrl ?? imageSrc ?? ''}
                            alt="正方形预览"
                            className="h-full w-full object-cover"
                          />
                        )}
                      </div>
                      <span className="text-muted text-xs font-medium">正方形预览</span>
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      <div
                        className="h-24 w-24 overflow-hidden rounded-full border border-[var(--border)]"
                        style={{ background: 'color-mix(in oklch, var(--surface-secondary) 50%, transparent)' }}
                      >
                        {(cropPreviewUrl ?? imageSrc) && (
                          <img
                            src={cropPreviewUrl ?? imageSrc ?? ''}
                            alt="圆形预览"
                            className="h-full w-full object-cover"
                          />
                        )}
                      </div>
                      <span className="text-muted text-xs font-medium">圆形预览</span>
                    </div>
                  </div>
                </div>
              </div>

              {uploadError ? <p className="text-danger text-sm">{uploadError}</p> : null}
            </Modal.Body>

            <Modal.Footer>
              <Button isDisabled={isUploading} variant="ghost" onPress={handleCancel}>
                取消
              </Button>
              <Button
                isDisabled={!imageSrc || isUploading}
                variant="primary"
                onPress={handleConfirm}
              >
                {isUploading ? '上传中…' : '确定'}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
